const FAMILY_NAME = /^[\w\s-]{1,64}$/;
const FONT_FILE = /\.(woff2?|ttf|otf)(\?|$)/i;

/** Merchant-supplied, so anything that isn't a plain family name must be https. */
const toUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

const inject = (id: string, create: () => HTMLElement) => {
  if (document.getElementById(id)) return;
  const el = create();
  el.id = id;
  document.head.appendChild(el);
};

const stylesheet = (href: string) => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  return link;
};

/**
 * Load `value` — a Google Fonts family name or an https URL to a stylesheet or
 * a font file — and point `cssVar` at the resulting family. Ignores anything
 * else. Safe to call repeatedly: each font is injected into <head> once.
 *
 * ponytail: fixed weights (400/500/600) and the latin subset. Widen only when a
 * merchant actually needs another subset.
 */
export const loadFont = (value: string, cssVar: string) => {
  const font = value.trim();
  const setFamily = (family: string) =>
    document.documentElement.style.setProperty(cssVar, `"${family}"`);
  const id = `font${cssVar}`;

  if (FAMILY_NAME.test(font)) {
    const family = font.replace(/\s+/g, " ");
    inject(id, () =>
      stylesheet(
        `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600&display=swap`,
      ),
    );
    return setFamily(family);
  }

  const url = toUrl(font);
  if (!url) return;

  // A font file has no family name of its own — give it one derived from the var.
  if (FONT_FILE.test(url.pathname)) {
    const family = `pc${cssVar.replace(/-+/g, "-")}`;
    inject(id, () => {
      const style = document.createElement("style");
      style.textContent = `@font-face{font-family:"${family}";src:url("${url.href.replace(/["')]/g, "")}");font-display:swap}`;
      return style;
    });
    return setFamily(family);
  }

  // Otherwise a stylesheet (e.g. a pasted Google Fonts css2 link).
  const family = url.searchParams.get("family")?.split(":")[0];
  if (!family) return;

  inject(id, () => stylesheet(url.href));
  setFamily(family.replace(/\+/g, " "));
};
